import React, { useCallback } from 'react';
import { GetMeQueryQueryResult, UpdateMeMutation, UpdateMeMutationResult, User, PasskeyCredential, UpdateRelation, useUpdateMeMutation, useGetMeQueryQuery, useRemoveOtherUserProxyMutation } from '../../generated/graphql';
import { useDiaEditor } from "../../components/DiaEditor";
import WaitSpinner from '../../components/wait-spinner';
import { NonIdealState } from '../../components/NonIdealState';
import { Accordion, AccordionSummary, AccordionDetails, TextField, IconButton, Alert, Button, ListItem, ListItemIcon } from '@mui/material';
import { VisibilityOff, Visibility, Save, Cancel, Fingerprint, Add } from '@mui/icons-material';
import { UserSelect } from '../../components/MaterialSelects/UserSelect';
import { useToast } from "../../contexts/toast";
import { Delete, Edit } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ModalWindow } from '../../components/ModalWindow';
import { Typography } from '@mui/material';
import { useDaybookContext } from '../../contexts/Daybook/DayBookContext';
import { useUserContext } from '../../contexts/user-context';

function blankValues(): User {
    return {
        id: '',
        proxyUser: null,
        password: '',
        usersIProxy: []
    };
}

const relationships = {
    proxyUser: 'connect',
    usersIproxy: 'connect',
    password: 'writeOnly',
    password2: 'transient'
};

export const Me = () => {
    const query = useGetMeQueryQuery();
    const { toast } = useToast();
    const [saveMutation, saveResult] = useUpdateMeMutation();
    const [removeProxyMutation, removeProxyResult] = useRemoveOtherUserProxyMutation();
    const [showPassword, setShowPassword] = React.useState(false);
    const [selectedProxy, setSelectedProxy] = React.useState(0);
    const daybook = useDaybookContext();
    const userContext = useUserContext();
    const passkeys = query?.data?.me?.passkeys ?? [];
    const [addDialogOpen, setAddDialogOpen] = React.useState(false);
    const [addName, setAddName] = React.useState('');
    const [renameTarget, setRenameTarget] = React.useState<PasskeyCredential | null>(null);
    const [renameName, setRenameName] = React.useState('');
    const save = useCallback(async ({ values, hasFailed, hasCompleted }) => {
        try {
            const result = await saveMutation({ variables: { input: values } });
            if (result.errors && result.errors.length > 0) {
                hasFailed();
            } else {
                hasCompleted();
            }
            return result;
        } catch (e) {
            hasFailed();
            throw e;
        }
    }, []);
    const getValue = useCallback((result: GetMeQueryQueryResult) => {
        return result?.data?.me as User;
    }, []);
    const getSaveValue = useCallback((result: UpdateMeMutationResult) => {
        return result?.data?.updateMe as User;
    }, []);


    const doDeleteUserProxy = async () => {
        await removeProxyMutation({ variables: { input: { ids: [selectedProxy as unknown as string] } } });
        setSelectedProxy(0);
        toast('User proxy removed', { variant: 'success' });
    };
    const openAddDialog = () => {
        setAddName('');
        setAddDialogOpen(true);
    };
    const confirmAdd = () => {
        const name = addName.trim();
        setAddDialogOpen(false);
        userContext.doPasskeyRegister(name || undefined);
    };
    const openRenameDialog = (p: PasskeyCredential) => {
        setRenameTarget(p);
        setRenameName(p.name ?? '');
    };
    const confirmRename = () => {
        if (renameTarget) {
            userContext.doPasskeyRename(renameTarget.id, renameName.trim());
        }
        setRenameTarget(null);
    };
    const passkeyLabel = (p: PasskeyCredential) => {
        if (p.name) return p.name;
        return p.created_at ? `Passkey · ${format(parseISO(p.created_at), 'MMM d, yyyy')}` : 'Passkey';
    };
    const data = useDiaEditor<User, GetMeQueryQueryResult, null, UpdateMeMutation>({
        save, saveResult, query: query,
        baseUrl: '/me',
        blankValues: blankValues,
        label: 'Options',
        permission: 'options',
        getValue: getValue,
        getSaveValue: getSaveValue,
        allowRedirectWhen: () => true
    });
    const values = data.form.values;

    if (query.error) {
        return <NonIdealState
            severity="error"
            title="An error has occurred"
            message="GraphQL error encountered"
        />;
    }
    if (data.form.status == 'loading') {
        return <WaitSpinner variant="panel" />;
    }
    const field = {
        onChange: data.form?.onChange
    };
    const handleClickShowPassword = () => {
        setShowPassword((v) => !v);
    }
    const doProxySave = async () => {
        const update: UpdateRelation = {};
        if (values.proxyUser) {
            update.connect = values.proxyUser.id;
        } else {
            update.disconnect = true;
        }
        await saveMutation({
            variables: {
                input: {
                    proxyUser: update
                }
            }
        });
        toast('User Proxy updated', { variant: 'success' });
    };
    const doPasswordSave = () => {
        const pw = values.password?.replace(' ', '');
        if (pw != values.password2) {
            toast('Passwords do not match', { variant: 'error' });
            return;
        }
        if (!pw || pw.length < 8) {
            toast('Password must be 8 characters or more', { variant: 'error' });
            return;
        }
        // normal form save path: the write-only password is sent by the diff
        // (GQLChanges) and reset by the form layer on success; no manual reset.
        data.form.handleSubmit();
    }
    const doProxyDelete = (id) => {

        return '';
    }
    return <div className="is-scrollable"><Accordion defaultExpanded={true}>
        <AccordionSummary>My Account</AccordionSummary>
        <AccordionDetails className="grid12">
            <TextField className="grid12-6" {...field} name="name" value={values?.name} label="Username" disabled={true} />
            <TextField className="grid12-6" {...field} name="email" value={values?.email} label="Email" disabled={true} />
        </AccordionDetails>
    </Accordion>
        <Accordion defaultExpanded={false}>
            <AccordionSummary>Change Password</AccordionSummary>
            <AccordionDetails className="grid12">
                <Alert severity="info">Changes to password will affect your next login.</Alert>
                <TextField label="New Password" {...field}
                    type={showPassword ? 'text' : 'password'}
                    value={values?.password ?? ''}
                    name="password"
                    className="grid12-8 grid12-clear"
                    InputProps={{
                        endAdornment:
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                edge="end"
                            >{showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                    }}
                />
                <TextField label="Repeat New Password" {...field}
                    type={showPassword ? 'text' : 'password'}
                    value={values?.password2 ?? ''}
                    name="password2"
                    className="grid12-8 grid12-clear"
                    InputProps={{
                        endAdornment:
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                edge="end"
                            >{showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                    }}
                />
                <div><Button onClick={doPasswordSave} variant="contained" color="primary"><Save /> Save</Button></div>
            </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded={false}>
            <AccordionSummary>Passkeys</AccordionSummary>
            <AccordionDetails className="grid12">
                <Alert severity="info">Passkeys let you sign in without a password. Use them on this device or with your platform authenticator.</Alert>
                {passkeys.length > 0 &&
                    <div>
                        {passkeys.map((p, idx) => (
                            <ListItem key={idx} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <span><Fingerprint style={{verticalAlign: 'middle', marginRight: 8}} />{passkeyLabel(p)}</span>
                                <span>
                                    <IconButton aria-label="rename passkey" onClick={() => openRenameDialog(p)}><Edit /></IconButton>
                                    <IconButton aria-label="remove passkey" onClick={() => userContext.doPasskeyDelete(p.id)}><Delete /></IconButton>
                                </span>
                            </ListItem>
                        ))}
                    </div>
                }
                <div><Button onClick={openAddDialog} variant="contained" color="primary" startIcon={<Add />}>Add passkey</Button></div>
            </AccordionDetails>
        </Accordion>
        <ModalWindow isOpen={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" header={
            <h3><Fingerprint /> Name this passkey</h3>
        } actions={
            <>
                <Button size="large" color="primary" onClick={confirmAdd} startIcon={<Add />}>Add passkey</Button>
                <Button size="large" color="primary" onClick={() => setAddDialogOpen(false)} startIcon={<Cancel />}>Cancel</Button>
            </>
        }>
            <TextField label="Name" fullWidth value={addName} onChange={(e) => setAddName(e.target.value)}
                placeholder="e.g. MacBook Touch ID" helperText="Optional. Used to tell your passkeys apart." autoFocus />
        </ModalWindow>
        <ModalWindow isOpen={!!renameTarget} onClose={() => setRenameTarget(null)} maxWidth="sm" header={
            <h3><Edit /> Rename passkey</h3>
        } actions={
            <>
                <Button size="large" color="primary" onClick={confirmRename} startIcon={<Save />}>Save</Button>
                <Button size="large" color="primary" onClick={() => setRenameTarget(null)} startIcon={<Cancel />}>Cancel</Button>
            </>
        }>
            <TextField label="Name" fullWidth value={renameName} onChange={(e) => setRenameName(e.target.value)}
                placeholder="e.g. MacBook Touch ID" helperText="Leave blank to clear the name." autoFocus />
        </ModalWindow>
        {daybook?.challengeEnabled &&
            <>
                <Accordion>
                    <AccordionSummary>Daybook User Proxy</AccordionSummary>
                    <AccordionDetails className="grid12">
                        <Alert severity="info">Assign a user to respond to any new challenge requests for your campaigns.  Note that this will not forward requests that someone else has forwarded to you!</Alert>
                        <UserSelect className="grid12-4" {...field} name="proxyUser" value={values.proxyUser} allowBlank={true} />
                        <div><Button disabled={values?.proxyUser?.id == data?.form?.initialValues?.proxyUser?.id} onClick={doProxySave} variant="contained" color="primary"><Save /> Save</Button></div>
                    </AccordionDetails>
                </Accordion>
                {values?.usersIProxy?.length > 0 &&
                    <Accordion>
                        <AccordionSummary>Daybook Inbound Proxies</AccordionSummary>
                        <AccordionDetails className="grid12">
                            <Alert severity="info">The following users are forwarding any requests to your account.  You may cancel the proxy below</Alert>
                            {
                                values?.usersIProxy?.map((v, idx) => {
                                    return (<ListItem onClick={() => setSelectedProxy(v.id)} key={idx}><ListItemIcon >
                                        <Delete />
                                    </ListItemIcon>{v.name}</ListItem>);
                                })
                            }
                        </AccordionDetails>
                    </Accordion>
                }
                <ModalWindow isOpen={selectedProxy > 0} onClose={() => { }} maxWidth="md" header={
                    <h3><Delete /> Delete User Proxy</h3>
                } actions={
                    <>
                        <Button size="large" color="warning" onClick={doDeleteUserProxy} startIcon={<Delete />}>Delete this proxy</Button>
                        <Button size="large" color="primary" onClick={() => { setSelectedProxy(0) }} startIcon={<Cancel />}>Cancel</Button>
                    </>
                }>
                    <Typography variant="body1">
                        <p>By deleting the proxy you will no longer receive requests for this user.</p>
                        <p>Note this cannot be undone without contacting an Admin</p>
                    </Typography>
                </ModalWindow>
            </>
        }
    </div>;
}
